import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  User,
  Settings,
  LogOut,
  LayoutDashboard,
  BookOpen,
  MessageSquare,
  Calendar,
  DollarSign,
  Shield,
  Sun,
  Moon,
  Monitor,
  Palette,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getFileUrl } from '@/lib/frappe';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';

export function UserMenu() {
  const { user, profile, isLoggedIn, isLoading, role, logout } = useAuth();
  const { theme, setMode, isDark } = useTheme();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Translations
  const t = {
    dashboard: language === 'ar' ? 'لوحة التحكم' : 'Dashboard',
    adminDashboard: language === 'ar' ? 'لوحة الإدارة' : 'Admin Dashboard',
    controlPanel: language === 'ar' ? 'لوحة التحكم' : 'Control Panel',
    schedule: language === 'ar' ? 'الجدول' : 'Schedule',
    myStudents: language === 'ar' ? 'طلابي' : 'My Students',
    earnings: language === 'ar' ? 'الأرباح' : 'Earnings',
    messages: language === 'ar' ? 'الرسائل' : 'Messages',
    mySessions: language === 'ar' ? 'جلساتي' : 'My Sessions',
    findTeachers: language === 'ar' ? 'ابحث عن معلم' : 'Find Teachers',
    profile: language === 'ar' ? 'الملف الشخصي' : 'Profile',
    settings: language === 'ar' ? 'الإعدادات' : 'Settings',
    logout: language === 'ar' ? 'تسجيل الخروج' : 'Logout',
    theme: language === 'ar' ? 'المظهر' : 'Theme',
    light: language === 'ar' ? 'فاتح' : 'Light',
    dark: language === 'ar' ? 'داكن' : 'Dark',
    system: language === 'ar' ? 'تلقائي' : 'System',
    admin: language === 'ar' ? 'مدير' : 'Admin',
    teacher: language === 'ar' ? 'معلم' : 'Teacher',
    student: language === 'ar' ? 'طالب' : 'Student',
    parent: language === 'ar' ? 'ولي أمر' : 'Parent',
    familyDashboard: language === 'ar' ? 'لوحة العائلة' : 'Family Dashboard',
  };

  if (isLoading) {
    return (
      <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
    );
  }

  if (!isLoggedIn || !user) {
    return null;
  }

  // Get initials for avatar fallback
  const getInitials = () => {
    if (user.full_name) {
      return user.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.email[0].toUpperCase();
  };

  // Get dashboard link based on role
  const getDashboardLink = () => {
    switch (role) {
      case 'admin':
        return '/admin';
      case 'teacher':
        return '/teacher';
      case 'student':
        return '/student';
      case 'parent':
        return '/family';
      default:
        return '/';
    }
  };

  // Get role display name
  const getRoleDisplayName = () => {
    switch (role) {
      case 'admin': return t.admin;
      case 'teacher': return t.teacher;
      case 'student': return t.student;
      case 'parent': return t.parent;
      default: return '';
    }
  };

  // Get role-specific menu items
  const getRoleMenuItems = () => {
    const iconClass = 'me-2 h-4 w-4';

    switch (role) {
      case 'admin':
        return (
          <>
            <DropdownMenuItem asChild>
              <Link to="/admin" className="flex items-center cursor-pointer">
                <LayoutDashboard className={iconClass} />
                {t.adminDashboard}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/control-panel" className="flex items-center cursor-pointer">
                <Shield className={iconClass} />
                {t.controlPanel}
              </Link>
            </DropdownMenuItem>
          </>
        );
      case 'teacher':
        return (
          <>
            <DropdownMenuItem asChild>
              <Link to="/teacher" className="flex items-center cursor-pointer">
                <LayoutDashboard className={iconClass} />
                {t.dashboard}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/teacher/schedule" className="flex items-center cursor-pointer">
                <Calendar className={iconClass} />
                {t.schedule}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/teacher/students" className="flex items-center cursor-pointer">
                <BookOpen className={iconClass} />
                {t.myStudents}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/teacher/earnings" className="flex items-center cursor-pointer">
                <DollarSign className={iconClass} />
                {t.earnings}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/teacher/chat" className="flex items-center cursor-pointer">
                <MessageSquare className={iconClass} />
                {t.messages}
              </Link>
            </DropdownMenuItem>
          </>
        );
      case 'student':
        return (
          <>
            <DropdownMenuItem asChild>
              <Link to="/student" className="flex items-center cursor-pointer">
                <LayoutDashboard className={iconClass} />
                {t.dashboard}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/student/schedule" className="flex items-center cursor-pointer">
                <Calendar className={iconClass} />
                {t.mySessions}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/teachers" className="flex items-center cursor-pointer">
                <BookOpen className={iconClass} />
                {t.findTeachers}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/student/chat" className="flex items-center cursor-pointer">
                <MessageSquare className={iconClass} />
                {t.messages}
              </Link>
            </DropdownMenuItem>
          </>
        );
      case 'parent':
        return (
          <>
            <DropdownMenuItem asChild>
              <Link to="/family" className="flex items-center cursor-pointer">
                <LayoutDashboard className={iconClass} />
                {t.familyDashboard}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/teachers" className="flex items-center cursor-pointer">
                <BookOpen className={iconClass} />
                {t.findTeachers}
              </Link>
            </DropdownMenuItem>
          </>
        );
      default:
        return null;
    }
  };

  // Get theme icon
  const getThemeIcon = () => {
    if (theme.mode === 'light') return <Sun className="h-4 w-4" />;
    if (theme.mode === 'dark') return <Moon className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  // Get profile image URL
  const profileImage = profile && 'profile_image' in profile
    ? getFileUrl(profile.profile_image || '')
    : user.user_image
      ? getFileUrl(user.user_image)
      : '';

  const iconClass = 'me-2 h-4 w-4';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-10 w-10 rounded-full"
          aria-label={`${user.full_name} – ${isRTL ? 'قائمة الحساب' : 'account menu'}`}
        >
          <Avatar className="h-10 w-10">
            <AvatarImage src={profileImage} alt={user.full_name} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align={isRTL ? 'start' : 'end'} forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.full_name}</p>
            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
            <p className="text-xs leading-none text-primary mt-1">
              {getRoleDisplayName()}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {getRoleMenuItems()}

        <DropdownMenuSeparator />

        {/* Parents have no separate profile page — their details live on the
            family account itself. */}
        {role !== 'parent' && (
          <DropdownMenuItem asChild>
            <Link to={`/${role === 'admin' ? 'admin' : role}/profile`} className="flex items-center cursor-pointer">
              <User className={iconClass} />
              {t.profile}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex items-center cursor-pointer">
            <Settings className={iconClass} />
            {t.settings}
          </Link>
        </DropdownMenuItem>

        {/* Theme Submenu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <Palette className={iconClass} />
            {t.theme}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme.mode} onValueChange={(value) => setMode(value as 'light' | 'dark' | 'system')}>
              <DropdownMenuRadioItem value="light" className="cursor-pointer">
                <Sun className={iconClass} />
                {t.light}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" className="cursor-pointer">
                <Moon className={iconClass} />
                {t.dark}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system" className="cursor-pointer">
                <Monitor className={iconClass} />
                {t.system}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleLogout}
          className="text-red-600 focus:text-red-600 cursor-pointer"
        >
          <LogOut className={iconClass} />
          {t.logout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserMenu;
